import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Dumbbell } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a personal trainer invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A personal trainer invoice should include your name and contact details, the client name, a unique invoice number and date, payment due date, a description of sessions (number of sessions, session type, and dates), your rate per session or monthly package fee, any cancellation fees for missed sessions, applicable tax if registered, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How often should personal trainers send invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'This depends on your billing arrangement. Many trainers bill weekly or monthly for a set number of sessions. Others bill per session immediately after. For package deals (e.g. 10-session blocks), invoice at the start of the block. Monthly billing with a fixed session count is the most common for ongoing clients as it creates predictable cash flow.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I charge for cancelled sessions on an invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if your cancellation policy covers it. Add a separate line item for "Late cancellation fee" and reference your cancellation policy (e.g., "Less than 24 hours notice per agreement"). Make sure clients have signed or acknowledged your policy before you charge cancellation fees. Clear terms upfront prevent disputes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do personal trainers need to charge VAT or GST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Only if you are registered. In the UK, VAT applies at 20% once your turnover exceeds £90,000. In Australia, add 10% GST if your turnover exceeds AUD $75,000. In India, fitness and personal training services attract 18% GST once you exceed the ₹20 lakh threshold. Many independent personal trainers operate below these thresholds and do not need to charge tax.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this personal trainer invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free personal trainer invoice template. Fill in your session details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function PersonalTrainerInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Personal Trainer Invoice Template: Free PDF for Fitness Trainers — UK & India | Invilic"
        description="Free personal trainer invoice template for UK and India fitness trainers. India GST SAC code 999312, UK 20% VAT rules. Bill sessions, packages. No sign-up needed."
        canonical="https://www.invilic.com/personal-trainer-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Personal Trainer Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for personal trainers and fitness coaches. Bill for sessions, packages, and online coaching. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Fitness Professional
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Personal trainers (gym & home)', desc: 'Bill clients for one-to-one sessions, package blocks, and programme design by the session or month.' },
              { title: 'Online fitness coaches', desc: 'Invoice for monthly coaching programmes, check-in calls, and nutrition plan deliverables.' },
              { title: 'Group fitness instructors', desc: 'Bill studios, gyms, or corporate clients for group class delivery and wellness sessions.' },
              { title: 'Sports & performance coaches', desc: 'Issue professional invoices for strength coaching, athletic development, and sports analysis.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center flex-shrink-0">
                  <Dumbbell className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Personal Trainer Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Trainer name and contact details',
              'Client name and address',
              'Invoice number and date',
              'Payment due date',
              'Sessions delivered and dates',
              'Session rate or package fee',
              'Online coaching or programme fee',
              'Cancellation fee (if applicable)',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
              'Notes and cancellation policy reference',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-orange-100 dark:border-orange-800/30 bg-orange-50 dark:bg-orange-900/10">
                <CheckCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Training Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your sessions', desc: 'Enter client details, sessions delivered, your rate, and any additional fees.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-orange-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Personal Trainer Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* India GST + UK VAT callout */}
        <section className="mb-16 grid sm:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl border border-emerald-100 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">GST on Personal Training in India</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Fitness and personal training services attract 18% GST in India once your annual turnover exceeds ₹20 lakh:
            </p>
            <ul className="space-y-2">
              {[
                'SAC code: 999312 — fitness and personal training services',
                '18% GST applies on all session and package fees',
                'Register for GST via the GST portal once turnover exceeds ₹20 lakh',
                'Below ₹20 lakh: issue standard invoices without GST',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-orange-100 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">Personal Trainer Invoicing in the UK</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              UK personal trainers operating as sole traders must know:
            </p>
            <ul className="space-y-2">
              {[
                'VAT: 20% standard rate, registration required above £90,000 turnover',
                'Self Assessment: register with HMRC if income exceeds £1,000/year',
                'Invoice must include: your name, invoice number, session description, fee, payment terms',
                'Keep all invoices for at least 5 years for HMRC records',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Training Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Personal Trainer Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
